@if (contributionYears) {
  @for (contributionsByYear of contributionYears.slice(0,maxSize); track $index) {
    <div class="contributions-year mb-3">

      <!-- Year Header (clickable toggle) -->
      <div class="contributions-year__header flex items-center gap-3 p-4 md:py-3 md:px-5 cursor-pointer select-none"
           [class.contributions-year__header--expanded]="isYearExpanded(contributionsByYear.yearOfContribution)"
           role="button" tabindex="0"
           (click)="toggleYear(contributionsByYear.yearOfContribution)"
           (keyup.enter)="toggleYear(contributionsByYear.yearOfContribution)">
        <div class="contributions-year__icon flex items-center justify-center w-9 h-9 md:w-8 md:h-8 text-[1.4rem] md:text-[1.3rem] shrink-0">
          <i class="fa fa-calendar-alt"></i>
        </div>
        <h4 class="contributions-year__title m-0 text-[1.8rem] md:text-[1.6rem] font-semibold">{{contributionsByYear.yearOfContribution}}</h4>
        @if (hasAnyContributions(contributionsByYear)) {
          <span class="contributions-year__count ml-auto text-[1.3rem] mr-2">
            {{getMonthsWithContributions(contributionsByYear).length}} {{'CALENDAR.months' | translate}}
          </span>
        }
        <i class="contributions-year__chevron fa fa-chevron-down text-[1.2rem] shrink-0"></i>
      </div>

      <!-- Year Body (collapsible) -->
      @if (isYearExpanded(contributionsByYear.yearOfContribution)) {
        <div class="contributions-year__body border border-t-0 rounded-b-lg p-5 md:p-3">
          @if (hasAnyContributions(contributionsByYear)) {
            <!-- Card Grid -->
            <div class="grid grid-cols-1 md:grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-2 md:gap-3">
              @for (item of getMonthsWithContributions(contributionsByYear); track item.month) {
                <div class="contribution-card flex flex-col rounded-md overflow-hidden cursor-pointer transition-colors duration-150"
                     [class.contribution-card--selected]="isSelected(contributionsByYear.yearOfContribution, item.month)"
                     role="button" tabindex="0"
                     (click)="onCellClick(contributionsByYear.yearOfContribution, item.month, contributionsByYear.monthlyContributions)"
                     (keyup.enter)="onCellClick(contributionsByYear.yearOfContribution, item.month, contributionsByYear.monthlyContributions)">
                  <div class="contribution-card__header flex items-center justify-between py-2 px-3 border-b">
                    <span class="contribution-card__month text-[1.3rem] font-medium capitalize">
                      {{'CALENDAR.full_month.' + item.month | translate}}
                    </span>
                    <i class="fa fa-chevron-down contribution-card__chevron text-[1rem]"></i>
                  </div>
                  <div class="contribution-card__body flex flex-row md:flex-col items-center justify-between md:justify-start gap-2 py-2 px-3 md:p-3">
                    <span class="contribution-card__code text-[1.1rem]">{{item.contribution.central}}</span>
                    <span class="contribution-card__amount text-[1.4rem] md:text-[1.5rem] font-semibold">{{item.contribution.membershipFee}}</span>
                  </div>

                  <!-- Mobile inline details (inside the card) -->
                  @if (isSelected(contributionsByYear.yearOfContribution, item.month)) {
                    <!-- click here only stops propagation to the card toggle; role=presentation keeps it out of the a11y tree as an interactive element -->
                    <div class="contribution-card__details block md:hidden p-3 border-t" role="presentation" (click)="$event.stopPropagation()">
                      @for (fee of selectedFees(); track $index) {
                        <div class="contribution-card__detail-item flex flex-col gap-1.5 p-2 rounded mb-2 last:mb-0">
                          <!--<div class="flex justify-between items-center">
                            <span class="contribution-card__detail-label text-[1.1rem]">{{'FINANCIAL.payment_date' | translate}}</span>
                            <span class="contribution-card__detail-value text-[1.2rem] font-medium">{{fee.dateOfPaymentDate | date:'dd/MM/yyyy'}}</span>
                          </div>-->
                          <div class="flex justify-between items-center">
                            <span class="contribution-card__detail-label text-[1.1rem]">{{'FINANCIAL.central' | translate}}</span>
                            <span class="contribution-card__detail-value text-[1.2rem] font-medium">{{getTranslatedData(fee.central) || '-'}}</span>
                          </div>
                          <div class="flex justify-between items-center">
                            <span class="contribution-card__detail-label text-[1.1rem]">{{'FINANCIAL.region' | translate}}</span>
                            <span class="contribution-card__detail-value text-[1.2rem] font-medium">{{getTranslatedData(fee.region) || '-'}}</span>
                          </div>
                          <div class="flex justify-between items-center">
                            <span class="contribution-card__detail-label text-[1.1rem]">{{'FINANCIAL.payment_method' | translate}}</span>
                            <span class="contribution-card__detail-value text-[1.2rem] font-medium">{{getTranslatedData(fee.paymentType) || '-'}}</span>
                          </div>
                          <div class="flex justify-between items-center">
                            <span class="contribution-card__detail-label text-[1.1rem]">{{'FINANCIAL.amount' | translate}}</span>
                            <span class="contribution-card__detail-value text-[1.2rem] font-medium">{{formatAmount(fee.amountOfContribution)}}</span>
                          </div>
                        </div>
                      }
                      @if (selectedFees().length === 0) {
                        <p class="contribution-card__detail-empty text-center py-3 text-[1.2rem] m-0">{{'GENERAL.no_data' | translate}}</p>
                      }
                    </div>
                  }
                </div>
              }
            </div>

            <!-- Desktop Detail Panel (outside the grid, hidden on mobile) -->
            @if (hasSelectedInYear(contributionsByYear.yearOfContribution)) {
              <div class="contribution-detail--desktop hidden md:block mt-4 p-4 rounded-md border">
                <div class="contribution-detail__header flex items-center justify-between mb-3 pb-3 border-b">
                  <span class="contribution-detail__title flex items-center gap-1.5">
                    <i class="fa fa-info-circle"></i>
                    {{'CALENDAR.full_month.' + selectedMonth() | translate}} {{selectedYear()}}
                  </span>
                  <button type="button" class="contribution-detail__close flex items-center justify-center w-6 h-6 p-0 bg-transparent border-0 text-[1.2rem] cursor-pointer transition-colors duration-150" (click)="closeDetails()">
                    <i class="fa fa-times"></i>
                  </button>
                </div>
                <div class="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-3">
                  @for (fee of selectedFees(); track $index) {
                    <div class="contribution-detail__item flex flex-col gap-1.5 p-3 rounded">
                      <!--<div class="flex justify-between items-center">
                        <span class="contribution-detail__label text-[1.1rem]">{{'FINANCIAL.payment_date' | translate}}</span>
                        <span class="contribution-detail__value text-[1.3rem] font-medium">{{fee.dateOfPaymentDate | date:'dd/MM/yyyy'}}</span>
                      </div>-->
                      <div class="flex justify-between items-center">
                        <span class="contribution-detail__label text-[1.1rem]">{{'FINANCIAL.central' | translate}}</span>
                        <span class="contribution-detail__value text-[1.3rem] font-medium">{{getTranslatedData(fee.central) || '-'}}</span>
                      </div>
                      <div class="flex justify-between items-center">
                        <span class="contribution-detail__label text-[1.1rem]">{{'FINANCIAL.region' | translate}}</span>
                        <span class="contribution-detail__value text-[1.3rem] font-medium">{{getTranslatedData(fee.region) || '-'}}</span>
                      </div>
                      <div class="flex justify-between items-center">
                        <span class="contribution-detail__label text-[1.1rem]">{{'FINANCIAL.payment_method' | translate}}</span>
                        <span class="contribution-detail__value text-[1.3rem] font-medium">{{getTranslatedData(fee.paymentType) || '-'}}</span>
                      </div>
                      <div class="flex justify-between items-center">
                        <span class="contribution-detail__label text-[1.1rem]">{{'FINANCIAL.amount' | translate}}</span>
                        <span class="contribution-detail__value text-[1.3rem] font-medium">{{formatAmount(fee.amountOfContribution)}}</span>
                      </div>
                    </div>
                  }
                  @if (selectedFees().length === 0) {
                    <p class="contribution-detail__empty text-center p-4 text-[1.3rem]">{{'GENERAL.no_data' | translate}}</p>
                  }
                </div>
              </div>
            }
          } @else {
            <div class="contributions-year__empty py-8 text-center text-[1.3rem]">
              <i class="fa fa-inbox block text-[2rem] mb-3"></i>
              {{ 'GENERAL.no_data' | translate }}
            </div>
          }
        </div>
      }

    </div>
  }
}

@if (!isLoading && !contributionYears?.length) {
  <div class="info-card-empty">
    @if (hasHiddenPreEuroYears) {
      <i class="fa fa-info-circle info-card-empty__icon"></i>
      <span>{{ 'FINANCIAL.pre_euro_none' | translate }}</span>
    } @else {
      <i class="fa fa-inbox info-card-empty__icon"></i>
      <span>{{ 'GENERAL.no_data' | translate }}</span>
    }
  </div>
}

@if (hasMoreToShow) {
  <div class="mt-6 text-center">
    <button class="c-button c-button--primary" (click)="showAll()">{{'FINANCIAL.show_all' | translate}}</button>
  </div>
}

<!-- Older years come last: the note closes the list, below the "show all" button -->
@if (!isLoading && contributionYears?.length && hasHiddenPreEuroYears) {
  <p class="contributions-note flex items-center gap-2 mt-4 mb-0 text-[1.3rem]">
    <i class="fa fa-info-circle"></i>
    <span>{{ 'FINANCIAL.pre_euro_hidden' | translate }}</span>
  </p>
}
